import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { createProtocolNote, type ProtocolNote } from "@/domains/pm/utils/protocol";
import type { Translations } from "@/lib/i18n/types";

type ProtocolCopy = Translations["pm"]["issues"]["protocol"];

interface ProtocolNotesFieldProps {
  notes: ProtocolNote[];
  copy: ProtocolCopy;
  onChange: (notes: ProtocolNote[]) => void;
}

/**
 * The sections the user writes themselves — repeatable heading + body rows,
 * modelled on the template form's person rows.
 *
 * The list starts empty rather than seeded with a blank row: an "Add section"
 * button reads as optional, where a pre-seeded empty row reads as a field
 * somebody forgot to fill.
 */
export function ProtocolNotesField({ notes, copy, onChange }: ProtocolNotesFieldProps) {
  const patch = (rowId: string, values: Partial<ProtocolNote>) =>
    onChange(notes.map((note) => (note.rowId === rowId ? { ...note, ...values } : note)));

  return (
    <div className="space-y-5">
      {notes.map((note) => (
        // Two plain white fields per section, bordered so each reads as
        // somewhere to type. No card around them: the pair already groups,
        // and a third box around a box was the chrome that made this heavy.
        <div key={note.rowId} className="space-y-2">
          <div className="flex items-center gap-2">
            <Input
              value={note.heading}
              aria-label={copy.noteHeadingLabel}
              placeholder={copy.noteHeadingPlaceholder}
              className="border-neutral-400 bg-background font-medium"
              onChange={(event) => patch(note.rowId, { heading: event.target.value })}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={copy.removeNote}
              className="shrink-0 text-muted-foreground hover:text-foreground"
              onClick={() => onChange(notes.filter((row) => row.rowId !== note.rowId))}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          {/* No maxLength: the renderer paginates a long note rather than
              clipping it, so a cap would be an invented rule whose first
              casualty loses text at the boundary with no warning. */}
          <Textarea
            rows={4}
            value={note.body}
            aria-label={copy.noteBodyLabel}
            placeholder={copy.noteBodyPlaceholder}
            className="resize-none border-neutral-400 bg-background"
            onChange={(event) => patch(note.rowId, { body: event.target.value })}
          />
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1.5"
        onClick={() => onChange([...notes, createProtocolNote()])}
      >
        <Plus aria-hidden className="h-4 w-4" />
        {copy.addNote}
      </Button>
    </div>
  );
}
